// Validation utility functions

export const validateMedicalInfo = (data, mandatoryOnly = false) => {
  const errors = {};

  // Mandatory fields - always validate
  if (!data.full_name?.trim()) {
    errors.full_name = 'Full Name is required';
  }

  if (!data.age) {
    errors.age = 'Age is required';
  }

  if (!data.gender) {
    errors.gender = 'Gender is required';
  }

  if (!data.blood_group) {
    errors.blood_group = 'Blood Group is required';
  }

  if (!data.mobile) {
    errors.mobile = 'Mobile is required';
  }

  if (!data.email) {
    errors.email = 'Email is required';
  }

  if (!data.medical_condition?.certificate) {
    errors.medical_certificate = 'Medical Prescription is required';
  }

  // Consent and Terms are mandatory always
  if (!data.consent) {
    errors.consent = 'Consent is required';
  }
  if (!data.terms_and_conditions) {
    errors.terms_and_conditions = 'You must accept Terms & Conditions';
  }

  // Optional fields - only validate during submission
  // Height and Weight are now mandatory irrespective of mandatoryOnly flag
  if (!data.weight) {
    errors.weight = 'Weight is required';
  }

  if (!data.height) {
    errors.height = 'Height is required';
  }

  if (!mandatoryOnly) {

    if (!data.medical_condition?.name?.trim()) {
      errors.medical_condition_name = 'Medical Condition Name is required';
    }

    if (!data.medical_condition?.notes?.trim()) {
      errors.medical_condition_notes = 'Medical Notes are required';
    }

    if (!data.medical_condition?.doctor_name?.trim()) {
      errors.medical_condition_doctor = 'Doctor Name is required';
    }

    if (!data.symbols || data.symbols.length === 0) {
      errors.symbols = 'Please select at least one tag';
    }

    if (!data.disability?.has_disability) {
      errors.has_disability = 'Please specify if your medical condition is a disability';
    }

    if (data.disability?.has_disability === 'yes') {
      if (!data.disability?.type) {
        errors.disability_type = 'Please specify if your disability is visible or invisible';
      }

      if (!data.disability?.has_certificate) {
        errors.has_certificate = 'Please specify if you have a disability certificate';
      }

      if (data.disability?.has_certificate === 'yes' && !data.disability?.certificate) {
        errors.disability_certificate = 'Please upload your disability certificate';
      }

      // Validate certificate format
      if (data.disability?.has_certificate === 'no' && data.disability?.certificate !== '') {
        errors.disability_certificate = 'Certificate should be empty when not having a disability certificate';
      }
    }
  }

  return errors;
};

export const validateEmergencyContact = (contacts, mandatoryOnly = false) => {
  const errors = [];

  // At least one emergency contact is mandatory
  if (contacts.length === 0) {
    errors[0] = { name: 'At least one emergency contact is required' };
    return errors;
  }

  contacts.forEach((contact, index) => {
    const contactErrors = {};

    // Mandatory fields - always validate first contact
    if (index === 0) {
      if (!contact.name?.trim()) {
        contactErrors.name = 'Primary Contact Name is required';
      }

      if (!contact.mobile?.trim()) {
        contactErrors.mobile = 'Primary Contact Phone number is required';
      } else if (!/^[0-9]{10}$/.test(contact.mobile.trim())) {
        contactErrors.mobile = 'Please enter a valid 10-digit phone number';
      }

      if (!contact.relationship) {
        contactErrors.relationship = 'Primary Contact Relationship is required';
      }
    } 
    // Optional fields - validate during submission or if partially filled
    else if (!mandatoryOnly && (contact.name || contact.mobile || contact.relationship)) {
      if (!contact.name?.trim()) {
        contactErrors.name = 'Contact Name is required';
      }
      if (!contact.mobile?.trim()) {
        contactErrors.mobile = 'Phone number is required';
      } else if (!/^[0-9]{10}$/.test(contact.mobile.trim())) {
        contactErrors.mobile = 'Please enter a valid 10-digit phone number';
      }

      if (!contact.relationship) {
        contactErrors.relationship = 'Relationship is required';
      }
    }

    if (Object.keys(contactErrors).length > 0) {
      errors[index] = contactErrors;
    }
  });

  return errors;
};

export const validateContactDetails = (data, mandatoryOnly = false) => {
  const errors = {};

  // Mandatory fields - always validate
  if (!data.mobile?.trim()) {
    errors.mobile = 'Phone number is required';
  } else if (!/^[0-9]{10}$/.test(data.mobile.trim())) {
    errors.mobile = 'Please enter a valid 10-digit phone number';
  }

  if (!data.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(data.email.trim())) {
    errors.email = 'Please enter a valid email address';
  }

  // Optional fields - only validate during submission
  if (!mandatoryOnly) {
    // Address validations
    if (!data.address?.pincode?.trim()) {
      errors['address.pincode'] = 'Pincode is required';
    } else if (!/^[0-9]{6}$/.test(data.address.pincode.trim())) {
      errors['address.pincode'] = 'Please enter a valid 6-digit pincode';
    }

    if (!data.address?.district?.trim()) {
      errors['address.district'] = 'District is required';
    }

    if (!data.address?.city?.trim()) {
      errors['address.city'] = 'City is required';
    }

    if (!data.address?.state?.trim()) {
      errors['address.state'] = 'State/UT is required';
    }

    if (!data.address?.line_1?.trim()) {
      errors['address.line_1'] = 'Address is required';
    }
  }

  return errors;
};

export const validateForm = (formData, step, mandatoryOnly = false) => {
  switch (step) {
    case 'medical':
      return validateMedicalInfo(formData, mandatoryOnly);
    case 'emergency':
      return validateEmergencyContact(formData.emergency_contacts, mandatoryOnly);
    case 'contact':
      return validateContactDetails(formData, mandatoryOnly);
    default:
      return {};
  }
};
